import { html, css, render } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { Grid, GridErrorEvent } from "@omegagrid/grid";
import { BaseVisualization, ReportEvent, VisualizationOptions } from "./baseVisualization";
import { TransactionAdapter } from "./transactionAdapter";
import { Button, ColorTypes, ComponentId, LayoutOptions, dom, numbers, SortableList, SortableListItem, DropdownContainer, ListSortEvent } from "@omegagrid/core";
import { getLocale, msg } from "@omegagrid/localize";
import { TransactionTotalItem } from "../../types";
import { guard } from "lit/directives/guard.js";
import { Statusbar } from "@omegagrid/statusbar";
import rp, { helpers } from "@reporting/rp-app-host";
import { createWorkbook, exportXLSX } from "@omegagrid/plugin-xlsx";
import { gridToDelimitedText } from "./gridDelimitedExport";

const COLUMN_SELECTOR_WIDTH = 200;

export type ColumnSettings = {
	column: string,
	hidden: boolean
};

@customElement('rp-visual-transactions')
export class TransactionVisualization extends BaseVisualization {

	static styles = css`
		:host {
			display: flex;
			flex-direction: column;
			width: 100%;
			height: 100%;
			position: relative;
		}

		.body {
			flex: 1;
			overflow: hidden;
			position: relative;
		}

		og-statusbar {
			flex: 0 0 22px;
		}

		og-grid {
			position: absolute;
			inset: 0;
		}
	`;

	@property({type: Object})
	adapter: TransactionAdapter;

	@state()
	totals: TransactionTotalItem[] = [];

	@query('og-grid')
	grid: Grid;

	@query('og-statusbar')
	statusbar: Statusbar;

	@state()
	error: string = null;

	private _toolbarInitialized = false;
	private _externalToolbar: HTMLDivElement;
	private _columnSelector: DropdownContainer;
	private _columnSelectorList: SortableList;
	private _columnSettings: ColumnSettings[];

	async loadColumnSettings() {
		const columns = await this.grid.model.columns;
		this._columnSettings = columns.map(col => ({
			column: col.name,
			hidden: col.hidden,
		}));

		this._columnSelectorList?.renderItems(true);
	}

	toggleColumnVisibility(index: number) {
		this.grid.model.columns[index].hidden = !this.grid.model.columns[index].hidden;
		this.grid.model.indexColumns();
		this.grid.rendering.render(true);
		this.loadColumnSettings();
		this._columnSettingsChanged = true;
	}

	moveColumn(index: number, newIndex: number) {
		const col = this.grid.model.columns.splice(index, 1)[0];
		this.grid.model.columns.splice(newIndex, 0, col);
		this.grid.model.indexColumns();
		this.grid.rendering.render(true);
		this.loadColumnSettings();
		this._columnSettingsChanged = true;
	}

	get columnSelectorList() {
		if (this._columnSelectorList) return this._columnSelectorList;
		this._columnSelectorList = dom.createElement<SortableList>('og-sortablelist', {
			slot: 'inner-content',
			style: {height: '100%'},
			listeners: [
				['click', (e: PointerEvent) => e.stopPropagation()],
				['list.sort', (e: ListSortEvent) => this.moveColumn(e.fromIndex, e.toIndex)]
			],
			customHandle: true,
			itemRenderer: (div: SortableListItem, index: number) => {
				const col = this.grid.model.columns[index];
				div.moveHandle = dom.createElement('div', {
					style: {cursor: 'grab'},
					innerHTML: '<og-icon icon="grip-dots-vertical"></og-icon>'
				});

				return html`
					<div style="display: flex; flex-direction: row; cursor: pointer;" @click="${() => this.toggleColumnVisibility(index)}">
						${div.moveHandle}
						<div style="flex: 0 0 20px"><og-checkbox .value="${col.hidden ? 'none' : 'checked'}"></og-checkbox></div>
						<div style="flex: 1">${col.header[0].label}</div>
					</div>
				`;
			},
		});
		return this._columnSelectorList;
	}

	get columnSelector() {
		if (this._columnSelector) return this._columnSelector;
		this._columnSelector = dom.createElement<DropdownContainer>('og-dropdown-container');
		this._columnSelector.appendChild(this.columnSelectorList);
		return this._columnSelector;
	}

	private _columnSettingsChanged = false;

	saveColumnSettings() {
		if (!this._columnSettingsChanged) return;
		this.adapter.updateColumns(this._columnSettings);
		this._columnSettingsChanged = false;
	}

	openColumnSelector(offsetElement: HTMLElement) {
		if (this.columnSelector.isConnected) return;
		// all columns are listed if they fit between the button and the top of the grid,
		// otherwise the list scrolls; anchored by the bottom so the dropdown adjusting its
		// own height on the first render cannot grow below the window (and scroll the page up)
		const rect = offsetElement.getBoundingClientRect();
		const maxHeight = rect.top - this.grid.getBoundingClientRect().top;
		this.columnSelector.maxHeight = maxHeight;
		document.body.append(this.columnSelector);
		dom.setSize(this.columnSelector, {
			w: COLUMN_SELECTOR_WIDTH,
			h: Math.min(this.columnSelectorList.itemHeight * this.columnSelectorList.size, maxHeight)
		});
		dom.setPosition(this.columnSelector, {
			b: document.body.clientHeight - rect.top,
			l: rect.right - COLUMN_SELECTOR_WIDTH
		});
		setTimeout(() => {
			document.body.addEventListener('click', () => {
				this.columnSelector.remove();
				this.saveColumnSettings();
			}, {once: true});
		}, 10);
	}

	updated(props: Map<string, any>) {
		(window as any).__dump_grid = (serialize: boolean) => {
			console.log(serialize ? JSON.stringify(this.grid.getSourceData()) : this.grid.getSourceData());
		}
	}

	_onLoad = async () => {
		await this.loadColumnSettings();
		this.columnSelectorList.size = this._columnSettings.length;

		this.totals = await this.adapter.getTotals();
		this.statusbar.leftItems = this.totals.map((total, i) => ({id: i, type: 'custom'}));
		this.statusbar.rightItems = [{id: 'columnSelector', type: 'custom'}];
		this.hideLoader();
		this.dispatchEvent(new ReportEvent('load'));
	}

	_onBeforeLoad = () => this.showLoader();

	copyTotal(index: number) {
		const value = this.totals[index].value;
		const formattedValue = numbers.getNumberFormatter(getLocale(), '#0.#####')(value);
		navigator.clipboard.writeText(this.totals[index].value.toString());
		rp.host.showAlert({
			content: msg('Copied to clipboard: {v}', {data: {v: formattedValue}}),
			type: 'info'
		});
	}

	_createStatusbarItem = (id: ComponentId) => {
		if (id == 'columnSelector') {
			const button = dom.createElement<Button>('og-button', {
				color: ColorTypes.Transparent,
				style: {height: '100%', display: 'block'},
				icon: 'columns',
				text: msg('Columns'),
			});
			button.addEventListener('click', () => this.openColumnSelector(button));
			return button;
			// return this.columnSelector;
		}

		const index = Number(id);
		const button = dom.createElement<Button>('og-button', {
			color: ColorTypes.Transparent,
			style: {height: '100%', display: 'block'},
			listeners: [['click', () => this.copyTotal(index)]]
		});

		render(html`
			<div slot="content" style="height: 100%; display: flex; align-items: center">
				<og-label .text="${msg(this.totals[index].name)}" style="height: 100%; max-width: 100px"></og-label>
				<div>:&nbsp;</div>
				<div>${numbers.getNumberFormatter(getLocale(), this.totals[index].format)(this.totals[index].value)}</div>
			</div>
		`, button);

		return button;
	};

	_onError = (e: GridErrorEvent) => {
		this.error = msg('Transaction loading error');
		this.hideLoader();
	}

	render = () => html`
		${this.error == null ? '' : html`<og-alert .text="${this.error}" type="danger"></og-alert>`}

		${guard(this.adapter, () => html`
			<div class="body">
				<og-grid
					.adapter="${this.adapter}"
					@grid.beforeLoad="${this._onBeforeLoad}"
					@grid.error="${this._onError}"
					@grid.afterLoad="${this._onLoad}">
				</og-grid>
			</div>
		`)}

		<og-statusbar .createComponent="${this._createStatusbarItem}"></og-statusbar>
	`;

	private initToolbar() {
		if (!this._externalToolbar) return;
		if (this._toolbarInitialized) return;
		this._toolbarInitialized = true;

		// const container = this._externalToolbar?.querySelector<HTMLDivElement>('.toolbar-right');
		// if (!container) return;

		// render(html`
		// 	<div style="text-align: right; display: flex; align-items: center; justify-content: right; height: 100%">
		// 		${this.columnSelector}
		// 	</div>
		// `, container);
	}

	disconnectedCallback(): void {
		super.disconnectedCallback();
		this.dispose();
	}

	dispose() {
		if (this._columnSelector) this._columnSelector.remove();
		this._columnSelector = null;
	}

	layout(options?: LayoutOptions) {
		if (!this._toolbarInitialized && options?.elements?.has('toolbar')) {
			this._externalToolbar = options.elements.get('toolbar') as HTMLDivElement;
			this.initToolbar();
		}
		this.grid?.layout();
	}

	getOptions(): VisualizationOptions|Promise<VisualizationOptions> {
		return this.createOptions({
			exportTypes: [
				{type: 'xlsx', icon: 'file-xls'},
				{type: 'csv', icon: 'file-csv'},
				{type: 'csv-raw', icon: 'file-csv', label: 'Export to csv (raw data)'},
				{type: 'tsv', icon: 'file-lines'},
				{type: 'tsv-raw', icon: 'file-lines', label: 'Export to tsv (raw data)'},
			]
		});
	}

	private async loadAllRows() {
		if (this.grid.model.rowRoot.isLoaded) return;
		const pageSize = this.adapter.pageSize;
		this.adapter.pageSize = 1000000; // Set a large page size to load all data
		this.grid.model.rowRoot.clearChildren();
		await this.grid.rendering.load();
		this.adapter.pageSize = pageSize; // Restore original page size
	}

	private createExportWorkbook() {
		return createWorkbook([this.grid.model], {
			defaultBorders: true,
			dropdownCells: "displayValue",
			exportHiddenColumns: false,
			exportHiddenRows: false,
			gridLines: false,
		});
	}

	async export(type: string) : Promise<any> {
		await this.loadAllRows();

		switch (type) {
			case 'xlsx':
				return this.createExportWorkbook();
			case 'csv':
			case 'csv-raw':
			case 'tsv':
			case 'tsv-raw':
				return gridToDelimitedText(this.grid.model, {
					separator: type.startsWith('tsv') ? '\t' : ',',
					raw: type.endsWith('-raw'),
				});
		}

		return null;
	}

	async download(type: string) {
		switch (type) {
			case 'xlsx':
				await exportXLSX(await this.export(type), helpers.createExportFilename(type));
				break;
			case 'csv':
			case 'csv-raw':
			case 'tsv':
			case 'tsv-raw': {
				// BOM keeps the UTF-8 encoding recognized by Excel
				const extension = type.split('-')[0];
				const blob = new Blob(['﻿' + await this.export(type)], {
					type: (extension == 'csv' ? 'text/csv' : 'text/tab-separated-values') + ';charset=utf-8'
				});
				const url = URL.createObjectURL(blob);
				try {
					helpers.downloadFromUrl(url, helpers.createExportFilename(extension));
				} finally {
					URL.revokeObjectURL(url);
				}
				break;
			}
		}
	}

}
